/**
 * 매장 소식 피드 — 보호자앱용. 내가 이용(재원)하는 매장이 올린 사진 소식.
 */
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";

export interface FeedPhoto {
  fileSeq: number;
  fileUrl: string | null;
  thumbnailUrl: string | null;
  thumbnailStatus: string | null;
}

export interface FeedItem {
  feedSeq: number;
  companySeq: number;
  companyName: string | null;
  companyFileUrl: string | null;
  authorName: string | null;
  content: string | null;
  dtEvent: string | null;
  viewCount: number;
  editedYn: boolean;
  dtRegist: string;
  photos: FeedPhoto[];
}

interface PageResponse<T> {
  content: T[];
  totalElements: number;
}

interface ServiceResult<T> {
  data: T;
}

export const customerFeedKeys = {
  list: ["customer-feed-list"] as const,
  detail: (id: number | undefined) => ["customer-feed-detail", id] as const,
};

const fetchFeed = async (page: number, size: number) => {
  const { data } = await privateHttp.get<ServiceResult<PageResponse<FeedItem>>>(
    API_ENDPOINTS.USER_FEED.LIST,
    { params: { page, size } },
  );
  return data.data;
};

const fetchFeedDetail = async (feedSeq: number) => {
  const { data } = await privateHttp.get<ServiceResult<FeedItem>>(
    API_ENDPOINTS.USER_FEED.DETAIL(feedSeq),
  );
  return data.data;
};

export const useCustomerFeed = (size = 10) => {
  return useInfiniteQuery({
    queryKey: [...customerFeedKeys.list, size],
    queryFn: ({ pageParam = 0 }) => fetchFeed(pageParam as number, size),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + (p.content?.length ?? 0), 0);
      if (loaded === 0 || loaded >= (lastPage.totalElements ?? 0))
        return undefined;
      return allPages.length;
    },
    staleTime: 30 * 1000,
  });
};

export const useCustomerFeedDetail = (feedSeq: number | undefined) => {
  return useQuery({
    queryKey: customerFeedKeys.detail(feedSeq),
    queryFn: () =>
      feedSeq == null
        ? Promise.resolve(null as FeedItem | null)
        : fetchFeedDetail(feedSeq),
    enabled: feedSeq != null && !Number.isNaN(feedSeq),
  });
};
